;!function(){try{var e="undefined"!=typeof globalThis?globalThis:"undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import type { LegalForm } from '../demo-data'
import { NL } from './nl'
import type { VisualCopy } from './types'

/**
 * The activity on a row, in the language of the page.
 *
 * `demo-data.ts` writes activity names once, in Dutch, because it is one universe shared by every
 * visual; this maps those names onto the copy tables rather than duplicating the data per locale.
 * Anything unrecognised passes through, which is what keeps a new row from silently rendering blank.
 */
export function activityLabel(t: VisualCopy, value: string | undefined): string | undefined {
  if (!value) return value
  const map: Record<string, string> = {
    Jaarrekening: t.yearActivity.jaarrekening.label,
    'Vennootschapsbelasting (VPB)': t.yearActivity.vpb.fullLabel ?? t.yearActivity.vpb.label,
    'KvK-publicatie': t.yearActivity.kvk.fullLabel ?? t.yearActivity.kvk.label,
    'Inkomstenbelasting (IB)': t.yearActivity.ib.fullLabel ?? t.yearActivity.ib.label,
    'Financiële administratie': t.monthActivity.administratie.fullLabel,
    Administratie: t.monthActivity.administratie.label,
    Omzetbelastingaangifte: t.monthActivity.omzetbelasting.fullLabel,
    Omzetbelasting: t.monthActivity.omzetbelasting.label,
    Salarisverwerking: t.monthActivity.salaris.fullLabel,
    Salaris: t.monthActivity.salaris.label,
    'Aangifte loonheffingen': t.monthActivity.loonheffingen.fullLabel,
    Loonheffingen: t.monthActivity.loonheffingen.label,
    'Kwaliteitsreview dossier': t.content.reviewActivity,
    'Publicatiestukken KvK': t.content.publicationDocs,
    'Suppletie omzetbelasting': t.content.vatCorrection,
    'Intern overleg': t.content.internalMeeting,
    'Jaarafsluiting administratie': t.content.yearEndClose,
    'VPB-aangifte': t.content.corporateTaxReturn,
  }
  return map[value] ?? value
}

/**
 * The status on a lead-time row: either a waiting state of its own or a workflow status.
 *
 * The waiting states are not part of the workflow the board moves through — they say the firm has
 * done its part and is held up by someone else — so they live in `content` rather than `workflow`.
 * Anything unrecognised still passes through untranslated; that keeps a new row visible rather than
 * blank, but it is also how "Wacht op klant" once ended up in Dutch on the English home page, so a
 * new status string belongs in this map on the same commit that adds it to the data.
 */
const LEAD_TIME_STATES: Record<string, (t: VisualCopy) => string> = {
  'Ter goedkeuring klant': (t) => t.content.awaitingClient,
  'Wacht op klant': (t) => t.content.awaitingClientReply,
  'Wacht op stukken': (t) => t.content.awaitingDocuments,
}

export function leadTimeStatus(t: VisualCopy, value: string): string {
  const state = LEAD_TIME_STATES[value]
  if (state) return state(t)
  const workflow = Object.entries(NL.workflow).find(([, label]) => label === value)
  return workflow ? t.workflow[workflow[0] as keyof VisualCopy['workflow']] : value
}

/** The uppercase chip beside an address ("ZAKELIJK"), in the language of the page. */
export function addressTagLabel(t: VisualCopy, value: string): string {
  return t.content.addressTag[value as keyof VisualCopy['content']['addressTag']] ?? value
}

/** A month named in the data ("Augustus"), in the language of the page. */
export function monthLabel(t: VisualCopy, value: string): string {
  const index = NL.monthsFull.findIndex((month) => month.toLowerCase() === value.toLowerCase())
  return index >= 0 ? t.monthsFull[index] : value
}

/** A client's legal form ("EENMANSZAAK"), as the page's language names it. */
export function legalFormLabel(t: VisualCopy, value: LegalForm): string {
  return t.content.legalForm[value] ?? value
}

/** An open task on the client dossier, in the language of the page; unknown titles pass through. */
export function dossierTaskLabel(t: VisualCopy, value: string): string {
  return t.content.dossierTasks[value] ?? value
}

/** A document on the client dossier, in the language of the page; unknown names pass through. */
export function fileLabel(t: VisualCopy, value: string): string {
  return t.content.files[value] ?? value
}

/** A cell of the spreadsheet the firm is leaving behind, in the language of the page. */
export function excelCellLabel(t: VisualCopy, value: string): string {
  return t.screen.excel.cells[value] ?? value
}
